/* eslint-disable max-statements */
// @refresh reset
import React, { FunctionComponent, useState, useCallback, useEffect } from 'react';
import {
    getDVRWindowLocationInProgramWindow,
    getLiveProgressFromPercentage,
    getLiveProgressPercentageBasedOnCurrentTime
} from '@24i/player-utils';
import { IThumbnail, SeekType } from '@24i/player-base';
import { useAutoUpdate, usePrevious, usePlayerTimes, useAdsData } from '@24i/player-utils-react';
import { useTheme } from 'Theme';
import { seekingStarted, seekingEnded, usePlayerUIContext } from '../../../Player/Context';

// USE /src imports to help next.js process less files to reduce bundle, avoid some problematic files, and speed up build.

import {
    Container,
    AbsoluteContainer,
    ProgressDot,
    DVRWindow,
    getDefaultTimelineStyles
} from './styled';
import { Buffered } from './components/Buffered';
import { SeekingManager } from './components/SeekingManager';
import { ProgressTime, getProgressTimeString } from './components/ProgressTime';
import { Midrolls } from './components/Midrolls';
import { Thumbnail } from '../Timeline/components/Thumbnail';
import { ITimeline } from '../Timeline/types';

const PERCENT = 100;

export const Timeline: FunctionComponent<ITimeline> = ({
    playerInstance,
    programRange,
    className = '',
    styles = {},
    dvrWindowOverride,
    shouldShowBufferedRanges = true,
    shouldShowProgressTime = false,
    showHours = true,
    variant,
    forceLiveTime = false,
    progressReplace = false,
    seekTo,
    progressInfo,
    isSyntheticChannel = false
}) => {
    const {
        state: { relativeSeekValue, isVolumeOpened, isSeeking, isOnLiveEdge },
        dispatch
    } = usePlayerUIContext();

    const { theme } = useTheme();

    const customStyles = {
        ...getDefaultTimelineStyles(theme),
        ...styles
    };

    const isLive = playerInstance?.live || false;
    const tick = useAutoUpdate();
    const { elapsedTime, durationTime } = usePlayerTimes(playerInstance);
    const previousRelativeSeekValue = usePrevious(relativeSeekValue);
    const timelineDomContainer = React.useRef<HTMLDivElement>(null);
    const [rawProgress, setProgress] = useState(0);
    const [hoverProgress, setHoverProgress] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const [thumbnail, setThumbnail] = useState<IThumbnail>();
    const [preloadStarted, setPreloadStarted] = useState(false);
    let preloadIteration = 0;

    const {
        isPlayingClientAd,
        isSeekingBackwardDisabled,
        isSeekingForwardDisabled } = useAdsData(playerInstance);
    const isSeekingDisabled = isSeekingBackwardDisabled && isSeekingForwardDisabled;
    const isActive = Boolean(isSeeking || isHovering || relativeSeekValue);
    let start = 0;
    let end = 0;
    const { seekableRange, timeshiftRange } = playerInstance ?? {};

    if (!playerInstance.live && !isSyntheticChannel) {
        start = seekableRange.start;
        end = seekableRange.end;
    } else if (isSyntheticChannel) {
        start = seekableRange.start;
        end =
            seekableRange.end -
            (playerInstance.getUTCTimestamp(seekableRange.end).getTime() - Date.now());
    } else {
        end = timeshiftRange.end;
        start = dvrWindowOverride ? timeshiftRange.end - dvrWindowOverride : timeshiftRange.start;
    }
    const { programStart: metadataStart, programEnd: metadataEnd } =
        playerInstance?.getMetadata() || {
            programStart: 0,
            programEnd: 0
        };

    const rangeStart = programRange?.start ?? metadataStart;
    const rangeEnd = programRange?.end ?? metadataEnd;

    const dvrWindowEndDate = playerInstance?.getUTCTimestamp(end)?.getTime() ?? Date.now();

    const dvrWindowLocationWithinProgram = getDVRWindowLocationInProgramWindow(
        isLive || isSyntheticChannel,
        rangeStart,
        rangeEnd,
        start,
        end,
        dvrWindowEndDate
    );

    const progress = Math.max(dvrWindowLocationWithinProgram.start, rawProgress);

    const seekToTime =
        isLive || isSyntheticChannel
            ? getLiveProgressFromPercentage(
                rangeStart,
                rangeEnd,
                start,
                end,
                progress / PERCENT,
                dvrWindowEndDate
            )
            : (progress / PERCENT) * durationTime;

    const thumbnailsPreload = async (timeEnd) => {
        const maxIterations = 100;
        const thumbnailsArray = await playerInstance.getThumbnails([timeEnd]);
        const preloadThumbnail = thumbnailsArray ? thumbnailsArray[0] : null;
        preloadIteration++;

        if (preloadThumbnail) {
            const img = new Image();
            img.src = preloadThumbnail.url;

            let nextImageTime = preloadThumbnail.end + 1;

            if (preloadThumbnail.imageHeight && preloadThumbnail.imageWidth) {
                const xQuantity = preloadThumbnail.imageWidth / preloadThumbnail.width;
                const yQuantity = preloadThumbnail.imageHeight / preloadThumbnail.height;
                const totalQty = xQuantity * yQuantity;
                nextImageTime =
                    preloadThumbnail.end +
                    totalQty * (preloadThumbnail.end - preloadThumbnail.start);
            }

            if (preloadThumbnail.end < durationTime && preloadIteration < maxIterations)
                thumbnailsPreload(nextImageTime * 1000);
        }
    };
    progressInfo.current = {
        progress,
        end: dvrWindowLocationWithinProgram.end,
        timeString: getProgressTimeString({
            progress:
                (shouldShowProgressTime || isSeeking || relativeSeekValue !== null) && progress,
            duration: durationTime,
            playerInstance,
            programRange,
            showHours,
            forceLiveTime
        })
    };
    const timeString = getProgressTimeString({
        progress: (shouldShowProgressTime || isSeeking || relativeSeekValue !== null) && progress,
        duration: durationTime,
        playerInstance,
        programRange,
        showHours,
        forceLiveTime
    });

    useEffect(
        () => () => {
            progressInfo.current = null;
        },
        []
    );
    useEffect(() => {
        if (isSeeking || (relativeSeekValue === null && previousRelativeSeekValue)) return;
        if (!isLive && !durationTime) return; // check NaN returned cases caused by % 0
        if (playerInstance?.paused) return;
        const playerCurrentTime = playerInstance?.currentTime ?? 0;
        const progressPercentage = isLive
            ? getLiveProgressPercentageBasedOnCurrentTime(
                rangeStart,
                rangeEnd,
                start,
                end,
                playerCurrentTime,
                relativeSeekValue,
                dvrWindowEndDate
            )
            : (playerCurrentTime + relativeSeekValue || 0) / durationTime;

        setProgress(
            parseFloat(Math.max(0, Math.min(100, progressPercentage * PERCENT || 0)).toFixed(3))
        );

        if (progress && !preloadStarted) {
            thumbnailsPreload(0);
            setPreloadStarted(true);
        }
    }, [relativeSeekValue, elapsedTime, tick]);

    useEffect(() => {
        progressInfo.current = false;
    }, [timeString]);

    useEffect(() => {
        if (!playerInstance || !playerInstance.currentTime) return;

        if (isSeeking || isHovering) {
            // Using an IIFE
            (async function getThumbnailsByHoverTime() {
                let time = seekToTime;
                if (isHovering) {
                    time = isLive
                        ? getLiveProgressFromPercentage(
                            rangeStart,
                            rangeEnd,
                            start,
                            end,
                            hoverProgress / PERCENT,
                            dvrWindowEndDate
                        )
                        : (hoverProgress / PERCENT) * durationTime;
                }

                try {
                    const thumbnails = []; // .getThumbnails([time]);
                    if (thumbnails && thumbnails.length) setThumbnail(thumbnails[0]);
                } catch (e) {
                    console.error('Error trying to get thumbnails: ', e);
                }
            })();
        }
    }, [isSeeking, isHovering, progress, hoverProgress]);

    const onSeekingStart = useCallback(() => {
        dispatch(seekingStarted());
        setProgress(parseFloat(hoverProgress.toFixed(3)));
    }, [hoverProgress]);

    const onSeekingEnd = useCallback(() => {
        if (!isSyntheticChannel) {
            seekTo({
                progress,
                end: dvrWindowLocationWithinProgram.end,
                timeString: getProgressTimeString({
                    progress:
                        (shouldShowProgressTime || isSeeking || relativeSeekValue !== null) &&
                        progress,
                    duration: durationTime,
                    playerInstance,
                    programRange,
                    showHours,
                    forceLiveTime
                })
            });
        }

        const maxSyntheticChannelSeek = Date.now() - rangeStart;

        playerInstance?.seek(
            isSyntheticChannel ? Math.min(seekToTime, maxSyntheticChannelSeek) : seekToTime,
            SeekType.CURRENT_TIME
        );
        dispatch(seekingEnded());
    }, [playerInstance, seekToTime]);

    const onSeekingProgress = useCallback(
        (seekingProgress) => {
            if (isSeeking) setProgress(parseFloat(seekingProgress.toFixed(3)));
            if (isHovering) setHoverProgress(parseFloat(seekingProgress.toFixed(3)));
        },
        [isSeeking, isHovering]
    );

    const finalIsOnLiveEdge =
        (isLive && isOnLiveEdge) ?? !!playerInstance?.isOnLiveEdge(seekToTime);
    const progressBarColor = finalIsOnLiveEdge
        ? customStyles.progressLive?.backgroundColor ?? 'transparent'
        : customStyles.progressBar?.backgroundColor || customStyles.progressDot?.backgroundColor;

    let canShowThumbnail = isHovering && hoverProgress && !isPlayingClientAd;

    if ((isLive || isSyntheticChannel) && canShowThumbnail) {
        canShowThumbnail = dvrWindowLocationWithinProgram.end > hoverProgress;
    }
    return (
        <Container
            testID="timeline"
            style={customStyles.container}
            ref={timelineDomContainer}
            className={`${className} Timeline`}
            onDoubleClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
            }}
            {...{ isActive, isVolumeOpened, variant }}
        >
            <AbsoluteContainer
                style={customStyles.backgroundContainer}
                className="Timeline-Container"
                data-test="Timeline-Container"
                progress={100}
            />
            {!!shouldShowBufferedRanges && (
                <Buffered
                    style={customStyles.buffered}
                    className="Timeline-Buffered"
                    data-test="Timeline-Buffered"
                    {...{ playerInstance }}
                    duration={durationTime}
                />
            )}
            {(((isLive || isSyntheticChannel) && !isPlayingClientAd) || progressReplace) && (
                <DVRWindow
                    style={{
                        backgroundColor: theme?.color?.progressBarLive,
                        ...customStyles.dvrWindow
                    }}
                    className="Timeline-DVRWindow"
                    data-test="Timeline-DVRWindow"
                    dvrWindowFromLeft={dvrWindowLocationWithinProgram.start}
                    dvrWindowFromRight={
                        progressReplace ? progressReplace.end : dvrWindowLocationWithinProgram.end
                    }
                />
            )}
            <AbsoluteContainer
                style={{ ...customStyles?.progressBar, backgroundColor: progressBarColor }}
                className="Timeline-Progress"
                data-test="Timeline-Progress"
                dvrWindowFromLeft={dvrWindowLocationWithinProgram.start}
                dvrWindowFromRight={
                    progressReplace ? progressReplace.progress : dvrWindowLocationWithinProgram.end
                }
                progress={progressReplace ? progressReplace.progress : progress}
            />
            {!isPlayingClientAd && (
                <Midrolls
                    style={customStyles.midrolls}
                    containerStyle={customStyles.midrollsContainer}
                    className="Timeline-Midrolls"
                    data-test="Timeline-Midrolls"
                    {...{ playerInstance }}
                />
            )}
            {!isPlayingClientAd && (
                <ProgressDot
                    style={customStyles.progressDot}
                    className="Timeline-ProgressDot"
                    data-test="Timeline-ProgressDot"
                    dvrWindowFromLeft={dvrWindowLocationWithinProgram.start}
                    dvrWindowFromRight={
                        progressReplace
                            ? progressReplace.progress
                            : dvrWindowLocationWithinProgram.end
                    }
                    progress={progressReplace ? progressReplace.progress : progress}
                    {...{ isActive, isVolumeOpened }}
                />
            )}
            <>
                <Thumbnail
                    styles={customStyles.thumbnail}
                    className="Timeline-Thumbnail"
                    progress={hoverProgress}
                    duration={durationTime}
                    isVisible={canShowThumbnail}
                    playerInstance={playerInstance}
                />
                {/* {!!thumbnail && (isSeeking || isHovering) && (
                    <Thumbnail
                        progress={
                            ((isSeeking || relativeSeekValue) && progress) ||
                            (isHovering && hoverProgress)
                        }
                        getTimelineElement={() => timelineDomContainer}
                        isVisible={isActive}
                        {...{
                            playerInstance,
                            thumbnail
                        }}
                    />
                )} */}
                <ProgressTime
                    styles={customStyles.progressTime}
                    className="Timeline-ProgressTime"
                    progress={
                        ((shouldShowProgressTime || isSeeking || relativeSeekValue) &&
                            (progressReplace ? progressReplace.progress : progress)) ||
                        (isHovering && hoverProgress)
                    }
                    isVisible={isActive || shouldShowProgressTime}
                    duration={durationTime}
                    forceLiveTime={forceLiveTime}
                    overrideTime={progressReplace ? progressReplace.timeString : false}
                    {...{ playerInstance, programRange, showHours }}
                />
            </>
            <SeekingManager
                parentElement={timelineDomContainer.current}
                onHoverEnd={() => {
                    setIsHovering(false);
                }}
                onHoverStart={() => {
                    setIsHovering(true);
                }}
                {...{
                    onSeekingStart,
                    onSeekingProgress,
                    onSeekingEnd,
                    isSeekingDisabled
                }}
            />
        </Container>
    );
};
